import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, CircularProgress, Alert, FormGroup, FormControlLabel, Checkbox, Typography, Menu, MenuItem, Button, useTheme, Fab, FormControl, Select, Chip, Drawer, useMediaQuery, IconButton } from '@mui/material';
import * as d3 from 'd3';
import { useDatabase } from '../../hooks/useDatabase';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  type: 'contact' | 'initiative' | 'organization';
  size: number;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
  status?: string;
  hidden?: boolean;
}

interface Link extends d3.SimulationLinkDatum<Node> {
  source: string | Node;
  target: string | Node;
  value: number;
  type: 'initiative' | 'association';
}

interface RelationshipToggle {
  id: string;
  label: string;
  type: 'initiative' | 'association';
  enabled: boolean;
}

export function Graph() {
  const svgRef = useRef<SVGSVGElement>(null);
  const { viewingAsUserId } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomTransformRef = useRef<d3.ZoomTransform | null>(null);
  const db = useDatabase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [graphData, setGraphData] = useState<{ nodes: Node[], links: Link[] } | null>(null);
  const [relationshipToggles, setRelationshipToggles] = useState<RelationshipToggle[]>([
    {
      id: 'initiative-contacts',
      label: 'Initiative-Contact',
      type: 'initiative',
      enabled: true
    },
    {
      id: 'organization-contacts',
      label: 'Organization-Contact',
      type: 'association',
      enabled: true
    },
    {
      id: 'initiative-initiative',
      label: 'Initiative-Initiative',
      type: 'association',
      enabled: true
    },
    {
      id: 'contact-contact',
      label: 'Contact-Contact',
      type: 'association',
      enabled: true
    }
  ]);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    node: Node | null;
  } | null>(null);
  const simulationRef = useRef<d3.Simulation<Node, Link> | null>(null);
  const [isWiggling, setIsWiggling] = useState(false);
  const wiggleIntervalRef = useRef<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Get focus, isolation, active initiatives states, degree, and hidden nodes from URL
  const focusedNodeId = searchParams.get('focus');
  const isolatedNodeId = searchParams.get('isolate');
  const activeOnly = searchParams.get('active') === 'true';
  const degree = parseInt(searchParams.get('degree') || '2', 10);
  const hiddenNodes = React.useMemo(() => new Set(searchParams.get('hidden')?.split(',') || []), [searchParams]);

  // Function to update URL parameters
  const updateUrlParams = React.useCallback((params: { 
    focus?: string | null; 
    isolate?: string | null; 
    active?: boolean;
    degree?: number;
    hidden?: string[] | null;
  }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (params.focus === null) {
      newParams.delete('focus');
    } else if (params.focus !== undefined) {
      newParams.set('focus', params.focus);
    }
    
    if (params.isolate === null) {
      newParams.delete('isolate');
    } else if (params.isolate !== undefined) {
      newParams.set('isolate', params.isolate);
    }

    if (params.active === undefined) {
      // Don't modify the active parameter
    } else if (params.active === false) {
      newParams.delete('active');
    } else {
      newParams.set('active', 'true');
    }

    if (params.degree === undefined) {
      // Don't modify the degree parameter
    } else if (params.degree === 1) {
      newParams.set('degree', '1');
    } else if (params.degree === 2) {
      newParams.set('degree', '2');
    } else if (params.degree === 3) {
      newParams.set('degree', '3');
    }

    if (params.hidden === null) {
      newParams.delete('hidden');
    } else if (params.hidden !== undefined) {
      if (params.hidden.length > 0) {
        newParams.set('hidden', params.hidden.join(','));
      } else {
        newParams.delete('hidden');
      }
    }
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Function to get connected nodes up to specified degree
  const getConnectedNodes = (nodeId: string, nodes: Node[], links: Link[], maxDegree: number): Set<string> => {
    const connectedNodes = new Set<string>();
    const degrees = new Map<string, number>();
    
    // Add the selected node with degree 0
    connectedNodes.add(nodeId);
    degrees.set(nodeId, 0);

    // Process nodes up to maxDegree
    for (let currentDegree = 0; currentDegree < maxDegree; currentDegree++) {
      // Get all nodes at current degree
      const currentNodes = Array.from(degrees.entries())
        .filter(([_, d]) => d === currentDegree)
        .map(([id]) => id);

      // For each node at current degree, find its connections
      currentNodes.forEach(currentNodeId => {
        links.forEach(link => {
          const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
          const targetId = typeof link.target === 'string' ? link.target : link.target.id;
          
          // Check both directions of the link
          [sourceId, targetId].forEach(connectedId => {
            if (connectedId === currentNodeId) {
              const otherId = sourceId === currentNodeId ? targetId : sourceId;
              // Only add if we haven't seen this node before
              if (!degrees.has(otherId)) {
                degrees.set(otherId, currentDegree + 1);
                connectedNodes.add(otherId);
              }
            }
          });
        });
      });
    }

    return connectedNodes;
  };

  // Function to focus on a specific node
  const focusNode = React.useCallback((node: Node) => {
    if (!simulationRef.current || !graphData) return;

    // Check if the node exists in the current graph data
    const nodeExists = graphData.nodes.some(n => n.id === node.id);
    if (!nodeExists) {
      console.warn(`Node ${node.id} not found in graph data`);
      return;
    }

    // Update URL with focus parameter
    updateUrlParams({ focus: node.id });

    // Reset all nodes' fixed positions
    graphData.nodes.forEach(n => {
      n.fx = null;
      n.fy = null;
    });

    // Find connected nodes
    const connectedNodeIds = new Set<string>();
    graphData.links.forEach(link => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      
      if (sourceId === node.id) connectedNodeIds.add(targetId);
      if (targetId === node.id) connectedNodeIds.add(sourceId);
    });

    try {
      // Adjust forces for better visualization with gentler transitions
      simulationRef.current
        .force('link', d3.forceLink<Node, Link>(graphData.links)
          .id((d: Node) => d.id)
          .distance((link: Link) => {
            const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
            const targetId = typeof link.target === 'string' ? link.target : link.target.id;
            return (sourceId === node.id || targetId === node.id) ? 120 : 100;
          }))
        .force('charge', d3.forceManyBody<Node>()
          .strength((d: d3.SimulationNodeDatum) => {
            return connectedNodeIds.has((d as Node).id) ? -300 : -400;
          }));
    } catch (error) {
      console.error('Error updating forces:', error);
    }
  }, [graphData, updateUrlParams]);

  // Function to unfocus the current node
  const unfocusNode = React.useCallback(() => {
    if (!simulationRef.current || !graphData) return;

    // Remove focus parameter from URL
    updateUrlParams({ focus: null });

    // Reset all nodes' fixed positions
    graphData.nodes.forEach(n => {
      n.fx = null;
      n.fy = null;
    });

    try {
      // Reset forces to default with gentler transitions
      simulationRef.current
        .force('link', d3.forceLink<Node, Link>(graphData.links)
          .id((d: Node) => d.id)
          .distance(100))
        .force('charge', d3.forceManyBody().strength(-300));
    } catch (error) {
      console.error('Error resetting forces:', error);
    }
  }, [graphData, updateUrlParams]);

  // Function to isolate a node
  const isolateNode = (node: Node) => {
    if (!graphData) return;
    updateUrlParams({ isolate: node.id, degree: 2 }); // Default to 2 degrees
  };

  // Function to clear isolation
  const clearIsolation = () => {
    updateUrlParams({ isolate: null, degree: 2 });
  };

  // Create visualization
  const createVisualization = React.useCallback((nodes: Node[], links: Link[], width: number, height: number) => {
    if (!svgRef.current) {
      console.error('SVG ref is not available');
      return;
    }

    // Function to get node degrees from isolated node
    const getNodeDegreesFromIsolated = (nodeId: string, nodes: Node[], links: Link[]): Map<string, number> => {
      const degrees = new Map<string, number>();
      degrees.set(nodeId!, 0);

      // Process nodes up to max degree
      for (let currentDegree = 0; currentDegree < degree; currentDegree++) {
        // Get all nodes at current degree
        const currentNodes = Array.from(degrees.entries())
          .filter(([_, d]) => d === currentDegree)
          .map(([id]) => id);

        // For each node at current degree, find its connections
        currentNodes.forEach(currentNodeId => {
          links.forEach(link => {
            const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
            const targetId = typeof link.target === 'string' ? link.target : link.target.id;
            
            // Check both directions of the link
            [sourceId, targetId].forEach(connectedId => {
              if (connectedId === currentNodeId) {
                const otherId = sourceId === currentNodeId ? targetId : sourceId;
                // Only add if we haven't seen this node before and it's not hidden
                if (!degrees.has(otherId) && !hiddenNodes.has(otherId)) {
                  degrees.set(otherId, currentDegree + 1);
                }
              }
            });
          });
        });
      }

      return degrees;
    };

    // Function to check if a node should be visible based on hidden nodes
    const shouldNodeBeVisible = (nodeId: string, nodeDegrees: Map<string, number>): boolean => {
      if (!isolatedNodeId || !hiddenNodes.size) return true;
      if (nodeId === isolatedNodeId) return true; // Always show isolated node
      if (hiddenNodes.has(nodeId)) return false;

      const nodeDegree = nodeDegrees.get(nodeId);
      if (nodeDegree === undefined) return false;

      // Check if this node has any connections to hidden nodes
      const hasHiddenConnections = links.some(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;
        
        if (sourceId === nodeId && hiddenNodes.has(targetId)) return true;
        if (targetId === nodeId && hiddenNodes.has(sourceId)) return true;
        return false;
      });

      if (!hasHiddenConnections) return true;

      // If node has hidden connections, check if it has alternative paths to isolated node
      // through non-hidden nodes
      const alternativePaths = links.some(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;
        
        if (sourceId === nodeId || targetId === nodeId) {
          const otherId = sourceId === nodeId ? targetId : sourceId;
          // Only consider paths through non-hidden nodes
          if (!hiddenNodes.has(otherId)) {
            const otherDegree = nodeDegrees.get(otherId);
            // Compare against the isolation degree setting
            return otherDegree !== undefined && otherDegree <= degree;
          }
        }
        return false;
      });

      return alternativePaths || false;
    };

    // Filter nodes and links if isolation is active
    let filteredNodes = nodes;
    let filteredLinks = links;

    // Filter out non-active initiatives if activeOnly is true
    if (activeOnly) {
      const activeNodeIds = new Set(
        filteredNodes
          .filter(node => node.type === 'initiative' && node.status === 'Active')
          .map(node => node.id)
      );

      // Keep all contacts and active initiatives
      filteredNodes = filteredNodes.filter(node => 
        node.type === 'contact' || node.type === 'organization' || activeNodeIds.has(node.id)
      );

      // Filter links to only include those where both nodes are visible
      filteredLinks = filteredLinks.filter(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;
        
        // Get the source and target nodes
        const sourceNode = filteredNodes.find(n => n.id === sourceId);
        const targetNode = filteredNodes.find(n => n.id === targetId);
        
        // If either node is not found, hide the link
        if (!sourceNode || !targetNode) return false;
        
        // If both nodes are contacts, show the link
        if (sourceNode.type === 'contact' && targetNode.type === 'contact') return true;
        
        // If either node is an organization, always show the link
        if (sourceNode.type === 'organization' || targetNode.type === 'organization') return true;
        
        // If either node is an initiative, it must be active
        if (sourceNode.type === 'initiative' && !activeNodeIds.has(sourceId)) return false;
        if (targetNode.type === 'initiative' && !activeNodeIds.has(targetId)) return false;
        
        return true;
      });
    }

    if (isolatedNodeId) {
      const connectedNodeIds = getConnectedNodes(isolatedNodeId, filteredNodes, filteredLinks, degree);
      
      // Filter nodes to only include connected ones
      filteredNodes = filteredNodes.filter(node => connectedNodeIds.has(node.id));
      
      // Filter links to only include those between connected nodes
      filteredLinks = filteredLinks.filter(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;
        return connectedNodeIds.has(sourceId) && connectedNodeIds.has(targetId);
      });
    }

    // Get node degrees from isolated node if isolation is active
    const nodeDegrees = isolatedNodeId ? getNodeDegreesFromIsolated(isolatedNodeId, filteredNodes, filteredLinks) : new Map();

    // Filter out hidden nodes and their connections
    if (hiddenNodes.size > 0 && isolatedNodeId) {
      filteredNodes = filteredNodes.filter(node => shouldNodeBeVisible(node.id, nodeDegrees));
      
      // Filter links to only include those between visible nodes
      filteredLinks = filteredLinks.filter(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;
        return shouldNodeBeVisible(sourceId, nodeDegrees) && shouldNodeBeVisible(targetId, nodeDegrees);
      });
    }

    console.log('Creating visualization:', { width, height, nodes: filteredNodes.length, links: filteredLinks.length });

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Create SVG with explicit dimensions
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    // Create a container group for all elements
    const container = svg.append('g')
      .attr('class', 'container');

    // Filter links based on toggle states
    filteredLinks = filteredLinks.filter(link => {
      // For initiative-contact relationships
      if (link.type === 'initiative') {
        const toggle = relationshipToggles.find(t => t.id === 'initiative-contacts');
        return toggle?.enabled ?? true;
      }
      // For other association relationships
      if (link.type === 'association') {
        const sourceNode = typeof link.source === 'string' ? filteredNodes.find(n => n.id === link.source) : link.source;
        const targetNode = typeof link.target === 'string' ? filteredNodes.find(n => n.id === link.target) : link.target;
        
        // For organization-contact relationships
        if ((sourceNode?.type === 'organization' && targetNode?.type === 'contact') ||
            (sourceNode?.type === 'contact' && targetNode?.type === 'organization')) {
          const toggle = relationshipToggles.find(t => t.id === 'organization-contacts');
          return toggle?.enabled ?? true;
        }
        
        if (sourceNode?.type === 'initiative' && targetNode?.type === 'initiative') {
          const toggle = relationshipToggles.find(t => t.id === 'initiative-initiative');
          return toggle?.enabled ?? true;
        }
        // For contact-contact relationships
        if (sourceNode?.type === 'contact' && targetNode?.type === 'contact') {
          const toggle = relationshipToggles.find(t => t.id === 'contact-contact');
          return toggle?.enabled ?? true;
        }
      }
      return true;
    });

    try {
      // Create simulation with filtered links
      const simulation = d3.forceSimulation<Node>(filteredNodes)
        .force('link', d3.forceLink<Node, Link>(filteredLinks)
          .id((d: Node) => d.id)
          .distance(100))
        .force('charge', d3.forceManyBody().strength(-300))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .alphaDecay(0.02) // Reduced from 0.05 for smoother transitions
        .velocityDecay(0.4); // Increased from 0.3 for smoother damping

      // Store simulation reference
      simulationRef.current = simulation;

      // Add wiggle effect if enabled
      if (isWiggling) {
        if (wiggleIntervalRef.current) {
          cancelAnimationFrame(wiggleIntervalRef.current);
        }
        
        // Create a smoother wiggle effect using requestAnimationFrame
        let lastTime = 0;
        const animate = (time: number) => {
          if (!isWiggling) return;
          
          const deltaTime = time - lastTime;
          if (deltaTime > 16) { // Cap at roughly 60fps
            lastTime = time;
            
            filteredNodes.forEach(node => {
              if (!node.fx && !node.fy) { // Only wiggle unfixed nodes
                // Use sine wave for smoother oscillation
                const t = time / 1000; // Convert to seconds
                const frequency = 0.5; // Oscillation frequency
                const amplitude = 0.3; // Movement amplitude
                
                // Add smooth oscillating forces
                node.vx = (node.vx || 0) + Math.sin(t * frequency + node.id.charCodeAt(0)) * amplitude;
                node.vy = (node.vy || 0) + Math.cos(t * frequency + node.id.charCodeAt(0)) * amplitude;
              }
            });
            
            simulation.alpha(0.1).restart();
          }
          
          wiggleIntervalRef.current = requestAnimationFrame(animate);
        };
        
        wiggleIntervalRef.current = requestAnimationFrame(animate);
      } else if (wiggleIntervalRef.current) {
        cancelAnimationFrame(wiggleIntervalRef.current);
        wiggleIntervalRef.current = null;
      }

      // Create links
      const linkElements = container.append('g')
        .attr('class', 'links')
        .selectAll('line')
        .data(filteredLinks)
        .enter()
        .append('line')
        .attr('stroke', theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)')
        .attr('stroke-opacity', (d: Link) => {
          if (d.type === 'initiative') {
            return 0.3; // Lighter opacity for initiative-contact lines
          }
          return 0.6; // Keep original opacity for other lines
        })
        .attr('stroke-dasharray', (d: Link): string => {
          if (d.type === 'association') {
            return '4,4'; // Dotted line for association links
          }
          return 'none'; // Solid line for initiative-contact links
        })
        .attr('stroke-width', (d: Link) => {
          const sourceId = typeof d.source === 'string' ? d.source : d.source.id;
          const targetId = typeof d.target === 'string' ? d.target : d.target.id;
          if (sourceId === focusedNodeId || targetId === focusedNodeId || 
              sourceId === isolatedNodeId || targetId === isolatedNodeId) {
            return 5;
          }
          return 1;
        });

      // Create nodes
      const nodeElements = container.append('g')
        .attr('class', 'nodes')
        .selectAll<SVGCircleElement, Node>('circle')
        .data(filteredNodes)
        .enter()
        .append('circle')
        .attr('r', (d: Node) => {
          // Make focused nodes the same size as isolated nodes
          if (d.id === isolatedNodeId || d.id === focusedNodeId) {
            return Math.sqrt(d.size) * 5;
          }
          return Math.sqrt(d.size) * 2;
        })
        .attr('fill', (d: Node) => {
          if (d.type === 'contact') {
            return theme.palette.mode === 'dark' 
              ? 'rgba(144, 202, 249, 0.8)'
              : 'rgba(33, 150, 243, 0.8)';
          } else if (d.type === 'organization') {
            return theme.palette.mode === 'dark' 
              ? 'rgba(156, 39, 176, 0.8)'
              : 'rgba(156, 39, 176, 0.8)';
          } else {
            return theme.palette.mode === 'dark'
              ? 'rgba(165, 214, 167, 0.8)'
              : 'rgba(76, 175, 80, 0.8)';
          }
        })
        .attr('stroke', theme.palette.background.paper)
        .attr('stroke-width', 2)
        .call(d3.drag<SVGCircleElement, Node>()
          .on('start', (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on('drag', (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on('end', (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }))
        .on('click', (event, d) => {
          event.stopPropagation();
          if (focusedNodeId === d.id) {
            unfocusNode();
          } else {
            focusNode(d);
          }
        })
        .on('contextmenu', (event, d) => {
          event.preventDefault();
          setContextMenu({
            mouseX: event.clientX,
            mouseY: event.clientY,
            node: d
          });
        })
        .on('touchstart', (event, d) => {
          event.preventDefault();
          const touch = event.touches[0];
          const longPressTimer = setTimeout(() => {
            setContextMenu({
              mouseX: touch.clientX,
              mouseY: touch.clientY,
              node: d
            });
          }, 500);

          const element = event.currentTarget;
          element.setAttribute('data-long-press-timer', longPressTimer.toString());

          element.addEventListener('touchend', function clearLongPressTimer() {
            const timerId = element.getAttribute('data-long-press-timer');
            if (timerId) {
              clearTimeout(parseInt(timerId));
              element.removeAttribute('data-long-press-timer');
            }
            element.removeEventListener('touchend', clearLongPressTimer);
          }, { once: true });
        });

      // Add labels
      const labelElements = container.append('g')
        .attr('class', 'labels')
        .selectAll('text')
        .data(filteredNodes)
        .enter()
        .append('text')
        .text((d: Node) => d.name)
        .attr('font-size', (d: Node) => {
          // Only apply enlarged font size if the node is isolated AND not focused
          if (d.id === isolatedNodeId && !focusedNodeId) {
            return 16;
          }
          if (d.id === focusedNodeId) {
            return 14;
          }
          return 12;
        })
        .attr('fill', theme.palette.text.primary)
        .attr('text-anchor', 'middle')
        .attr('dy', (d: Node) => {
          // Only apply enlarged spacing if the node is isolated AND not focused
          if (d.id === isolatedNodeId && !focusedNodeId) {
            return Math.sqrt(d.size) * 2.5 + 15;
          }
          if (d.id === focusedNodeId) {
            return Math.sqrt(d.size) * 2 + 15;
          }
          return Math.sqrt(d.size) * 2 + 15;
        });

      // Update positions on each tick
      simulation.on('tick', () => {
        linkElements
          .attr('x1', (d: any) => d.source.x)
          .attr('y1', (d: any) => d.source.y)
          .attr('x2', (d: any) => d.target.x)
          .attr('y2', (d: any) => d.target.y);

        nodeElements
          .attr('cx', (d: any) => d.x)
          .attr('cy', (d: any) => d.y);

        labelElements
          .attr('x', (d: any) => d.x)
          .attr('y', (d: any) => d.y);
      });

      // Click handler for background
      svg.on('click', () => {
        if (focusedNodeId) {
          unfocusNode();
        }
        setContextMenu(null);
      });

      // Add zoom behavior
      const zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 4])
        .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
          container.attr('transform', event.transform.toString());
          zoomTransformRef.current = event.transform;
        });

      svg.call(zoom);

      // Apply stored transform if it exists
      if (zoomTransformRef.current) {
        svg.call(zoom.transform, zoomTransformRef.current);
      }

      console.log('Visualization creation completed');
    } catch (error) {
      console.error('Error creating visualization:', error);
    }
  }, [isolatedNodeId, relationshipToggles, focusedNodeId, theme, focusNode, unfocusNode, activeOnly, degree, isWiggling, hiddenNodes]);

  // Clean up wiggle interval on unmount
  useEffect(() => {
    return () => {
      if (wiggleIntervalRef.current) {
        cancelAnimationFrame(wiggleIntervalRef.current);
      }
    };
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching data...');

        // Fetch contacts, initiatives, and organizations
        const { data: contacts, error: contactsError } = await db.getContacts();
        if (contactsError) throw contactsError;

        const { data: initiatives, error: initiativesError } = await db.getInitiatives();
        if (initiativesError) throw initiativesError;

        const { data: organizations, error: organizationsError } = await db.getOrganizations();
        if (organizationsError) throw organizationsError;

        console.log('Data fetched:', { contacts: contacts?.length, initiatives: initiatives?.length, organizations: organizations?.length });

        // Collect all contact IDs from initiatives that might not be in the contacts list
        const initiativeContactIds = new Set<string>();
        (initiatives || []).forEach(initiative => {
          initiative.contacts.forEach(contactId => {
            initiativeContactIds.add(contactId);
          });
        });

        // Fetch any missing contacts by their IDs
        const existingContactIds = new Set((contacts || []).map(c => c.id));
        const missingContactIds = Array.from(initiativeContactIds).filter(id => !existingContactIds.has(id));
        
        let allContacts = [...(contacts || [])];
        if (missingContactIds.length > 0) {
          const { data: missingContacts, error: missingContactsError } = await db.getContactsByIds(missingContactIds);
          if (!missingContactsError && missingContacts) {
            allContacts = [...allContacts, ...missingContacts];
          }
        }

        // Create nodes
        const nodes: Node[] = [
          ...allContacts.map(contact => ({
            id: contact.id,
            name: contact.name,
            type: 'contact' as const,
            size: 5
          })),
          ...(initiatives || []).map(initiative => ({
            id: initiative.id,
            name: initiative.name,
            type: 'initiative' as const,
            size: 8,
            status: initiative.status
          })),
          ...(organizations || []).map(organization => ({
            id: organization.id,
            name: organization.name,
            type: 'organization' as const,
            size: 10
          }))
        ];

        // Create a map of node IDs for quick lookup
        const nodeMap = new Map(nodes.map(node => [node.id, node]));

        // Create links array
        const links: Link[] = [];

        // Add initiative-contact links
        (initiatives || []).forEach(initiative => {
          initiative.contacts.forEach(contactId => {
            // Only create links if both nodes exist
            if (nodeMap.has(contactId) && nodeMap.has(initiative.id)) {
              links.push({
                source: contactId,
                target: initiative.id,
                value: 1,
                type: 'initiative'
              });
            }
          });

          // Add initiative-to-initiative links
          initiative.related_initiatives.forEach(relatedInitiativeId => {
            // Only create links if both nodes exist
            if (nodeMap.has(relatedInitiativeId) && nodeMap.has(initiative.id)) {
              links.push({
                source: initiative.id,
                target: relatedInitiativeId,
                value: 1,
                type: 'association'
              });
            }
          });
        });

        // Add organization-contact links via junction table
        // Fetch all contact-organization relationships in one query
        const { data: contactOrgs, error: contactOrgsError } = await db.getAllContactOrganizations();
        if (!contactOrgsError && contactOrgs) {
          contactOrgs.forEach(({ contact_id, organization_id }) => {
            if (nodeMap.has(contact_id) && nodeMap.has(organization_id)) {
              links.push({
                source: contact_id,
                target: organization_id,
                value: 1,
                type: 'association'
              });
            }
          });
        }

        // Add contact association links
        (contacts || []).forEach(contact => {
          contact.associated_contacts.forEach(associatedContactId => {
            // Only create links if both nodes exist and haven't been added yet
            if (nodeMap.has(associatedContactId)) {
              const linkExists = links.some(link => 
                (link.source === contact.id && link.target === associatedContactId) ||
                (link.source === associatedContactId && link.target === contact.id)
              );
              
              if (!linkExists) {
                links.push({
                  source: contact.id,
                  target: associatedContactId,
                  value: 1,
                  type: 'association'
                });
              }
            }
          });
        });

        console.log('Created graph data:', { nodes: nodes.length, links: links.length });
        setGraphData({ nodes, links });
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db, viewingAsUserId]);

  // Update visualization when data or dimensions change
  useEffect(() => {
    if (graphData) {
      createVisualization(graphData.nodes, graphData.links, dimensions.width, dimensions.height);
    }
  }, [graphData, dimensions, createVisualization]);

  // Update visualization when URL parameters change
  useEffect(() => {
    if (graphData) {
      createVisualization(graphData.nodes, graphData.links, dimensions.width, dimensions.height);
    }
  }, [graphData, dimensions, createVisualization, focusedNodeId, isolatedNodeId]);

  // Handle window resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleToggleChange = (toggleId: string) => {
    setRelationshipToggles(prev => 
      prev.map(toggle => 
        toggle.id === toggleId 
          ? { ...toggle, enabled: !toggle.enabled }
          : toggle
      )
    );
  };

  const handleClearFilters = () => {
    // Reset all relationship toggles to enabled
    setRelationshipToggles(prev => 
      prev.map(toggle => ({ ...toggle, enabled: true }))
    );
    // Clear focus, isolation, active filter, and hidden nodes from URL
    updateUrlParams({ focus: null, isolate: null, active: false, hidden: null });
  };

  // Function to toggle node visibility
  const toggleNodeVisibility = (nodeId: string) => {
    // Don't allow hiding the isolated node
    if (nodeId === isolatedNodeId) return;
    
    const newHidden = new Set(hiddenNodes);
    if (newHidden.has(nodeId)) {
      newHidden.delete(nodeId);
    } else {
      newHidden.add(nodeId);
    }
    updateUrlParams({ hidden: Array.from(newHidden) });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Galaxy">
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, backgroundColor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {!focusedNodeId && !isolatedNodeId && (
              <Chip
                label="Full Network"
                color="default"
                variant="outlined"
                sx={{ 
                  '& .MuiChip-deleteIcon': {
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                    },
                  },
                }}
              />
            )}
            {isolatedNodeId && (
              <Chip
                label={`Isolated: ${graphData?.nodes.find(n => n.id === isolatedNodeId)?.name || 'Unknown'} (${degree}${degree === 1 ? 'st' : degree === 2 ? 'nd' : 'rd'} degree)`}
                color="primary"
                variant="outlined"
                onDelete={() => clearIsolation()}
                sx={{ 
                  '& .MuiChip-deleteIcon': {
                    color: 'primary.main',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  },
                }}
              />
            )}
            {focusedNodeId && (
              <Chip
                label={`Focused: ${graphData?.nodes.find(n => n.id === focusedNodeId)?.name || 'Unknown'}`}
                color="secondary"
                variant="outlined"
                onDelete={() => unfocusNode()}
                sx={{ 
                  '& .MuiChip-deleteIcon': {
                    color: 'secondary.main',
                    '&:hover': {
                      color: 'secondary.dark',
                    },
                  },
                }}
              />
            )}
            {Array.from(hiddenNodes).map(nodeId => {
              const node = graphData?.nodes.find(n => n.id === nodeId);
              return (
                <Chip
                  key={nodeId}
                  label={`Hidden: ${node?.name || 'Unknown'}`}
                  color="error"
                  variant="outlined"
                  onDelete={() => {
                    const newHidden = new Set(hiddenNodes);
                    newHidden.delete(nodeId);
                    updateUrlParams({ hidden: Array.from(newHidden) });
                  }}
                  sx={{ 
                    '& .MuiChip-deleteIcon': {
                      color: 'error.main',
                      '&:hover': {
                        color: 'error.dark',
                      },
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Paper 
          sx={{ 
            width: '100%', 
            mb: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
            position: 'relative'
          }}
        >
          {!isMobile && (
            <FormGroup row sx={{ mb: 2 }}>
              {relationshipToggles.map((toggle) => (
                <FormControlLabel
                  key={toggle.id}
                  control={
                    <Checkbox
                      checked={toggle.enabled}
                      onChange={() => handleToggleChange(toggle.id)}
                      sx={{
                        color: 'text.secondary',
                        '&.Mui-checked': {
                          color: toggle.type === 'initiative' ? 'primary.main' : 'secondary.main',
                          backgroundColor: 'transparent',
                        },
                        '& .MuiSvgIcon-root': {
                          backgroundColor: 'background.paper',
                          borderRadius: '4px',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.primary">
                      {toggle.label}
                    </Typography>
                  }
                  sx={{ mr: 2 }}
                />
              ))}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={activeOnly}
                    onChange={(e) => updateUrlParams({ active: e.target.checked })}
                    sx={{
                      color: 'text.secondary',
                      '&.Mui-checked': {
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                      },
                      '& .MuiSvgIcon-root': {
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    Active Initiatives Only
                  </Typography>
                }
                sx={{ mr: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isWiggling}
                    onChange={(e) => setIsWiggling(e.target.checked)}
                    sx={{
                      color: 'text.secondary',
                      '&.Mui-checked': {
                        color: 'success.main',
                        backgroundColor: 'transparent',
                      },
                      '& .MuiSvgIcon-root': {
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    Motion
                  </Typography>
                }
                sx={{ mr: 2 }}
              />
              {isolatedNodeId && (
                <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
                  <Select
                    value={degree}
                    onChange={(e) => updateUrlParams({ degree: e.target.value as number })}
                    sx={{
                      height: 32,
                      '& .MuiSelect-select': {
                        py: 0.5,
                      },
                    }}
                  >
                    <MenuItem value={1}>1st Degree</MenuItem>
                    <MenuItem value={2}>2nd Degree</MenuItem>
                    <MenuItem value={3}>3rd Degree</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                sx={{ 
                  height: 32,
                  minWidth: 120,
                  '& .MuiButton-label': {
                    py: 0.5,
                  },
                }}
              >
                Clear Filters
              </Button>
            </FormGroup>
          )}

          <Box
            ref={containerRef}
            sx={{
              width: '100%',
              height: isFullscreen ? '100vh' : (isMobile ? 'calc(100vh - 200px)' : 'calc(100vh - 300px)'),
              minHeight: isFullscreen ? '100vh' : 400,
              position: 'relative',
              backgroundColor: 'background.paper',
              borderRadius: isFullscreen ? 0 : 1,
              overflow: 'hidden',
            }}
          >
            <svg
              ref={svgRef}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
            
            {/* Filter FAB - show on mobile */}
            {isMobile && (
              <Fab
                size="medium"
                color="primary"
                onClick={() => setDrawerOpen(true)}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  zIndex: 1000
                }}
              >
                <FilterListIcon />
              </Fab>
            )}
            
            {/* Context menu FAB - show when node is focused or isolated */}
            {(focusedNodeId || isolatedNodeId) && (
              <Fab
                size="medium"
                color="primary"
                onClick={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect();
                  const node = graphData?.nodes.find(n => n.id === (focusedNodeId || isolatedNodeId));
                  if (node) {
                    setContextMenu({
                      mouseX: rect.left,
                      mouseY: rect.top,
                      node: node
                    });
                  }
                }}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  zIndex: 1000
                }}
              >
                <MoreVertIcon />
              </Fab>
            )}

            {/* Fullscreen button */}
            <IconButton
              onClick={() => {
                if (isFullscreen) {
                  document.exitFullscreen();
                  setIsFullscreen(false);
                } else {
                  if (containerRef.current) {
                    containerRef.current.requestFullscreen();
                    setIsFullscreen(true);
                  }
                }
              }}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'background.paper',
                },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
        </Paper>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 320,
            p: 2,
          }
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filters & Options
          </Typography>
          <FormGroup>
            {relationshipToggles.map((toggle) => (
              <FormControlLabel
                key={toggle.id}
                control={
                  <Checkbox
                    checked={toggle.enabled}
                    onChange={() => handleToggleChange(toggle.id)}
                    sx={{
                      color: 'text.secondary',
                      '&.Mui-checked': {
                        color: toggle.type === 'initiative' ? 'primary.main' : 'secondary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    {toggle.label}
                  </Typography>
                }
              />
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={activeOnly}
                  onChange={(e) => updateUrlParams({ active: e.target.checked })}
                  sx={{
                    color: 'text.secondary',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.primary">
                  Active Initiatives Only
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isWiggling}
                  onChange={(e) => setIsWiggling(e.target.checked)}
                  sx={{
                    color: 'text.secondary',
                    '&.Mui-checked': {
                      color: 'success.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.primary">
                  Motion
                </Typography>
              }
            />
            {isolatedNodeId && (
              <FormControl size="small" sx={{ mt: 2, width: '100%' }}>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Connection Degree
                </Typography>
                <Select
                  value={degree}
                  onChange={(e) => updateUrlParams({ degree: e.target.value as number })}
                  fullWidth
                >
                  <MenuItem value={1}>1st Degree</MenuItem>
                  <MenuItem value={2}>2nd Degree</MenuItem>
                  <MenuItem value={3}>3rd Degree</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                handleClearFilters();
                setDrawerOpen(false);
              }}
              sx={{ mt: 2 }}
            >
              Clear Filters
            </Button>
          </FormGroup>
        </Box>
      </Drawer>

      <Menu
        open={contextMenu !== null}
        onClose={() => {
          setContextMenu(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        {contextMenu?.node && (
          <>
            <MenuItem onClick={() => {
              if (contextMenu.node) {
                navigate(`/${contextMenu.node.type}s/${contextMenu.node.id}`);
                setContextMenu(null);
              }
            }}>
              View Details
            </MenuItem>
            {contextMenu.node.id === focusedNodeId ? (
              <MenuItem onClick={() => {
                unfocusNode();
                setContextMenu(null);
              }}>
                Unfocus Node
              </MenuItem>
            ) : (
              <MenuItem onClick={() => {
                if (contextMenu.node) {
                  focusNode(contextMenu.node);
                  setContextMenu(null);
                }
              }}>
                Focus Node
              </MenuItem>
            )}
            <MenuItem onClick={() => {
              if (contextMenu.node) {
                isolateNode(contextMenu.node);
                setContextMenu(null);
              }
            }}>
              Isolate Node
            </MenuItem>
            {isolatedNodeId && contextMenu.node.id !== isolatedNodeId && (
              <MenuItem onClick={() => {
                if (contextMenu.node) {
                  toggleNodeVisibility(contextMenu.node.id);
                  setContextMenu(null);
                }
              }}>
                {hiddenNodes.has(contextMenu.node.id) ? 'Show Node' : 'Hide Node'}
              </MenuItem>
            )}
            {isolatedNodeId && (
              <MenuItem onClick={() => {
                clearIsolation();
                setContextMenu(null);
              }}>
                Clear Isolation
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </DashboardLayout>
  );
} 